import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { Button } from '~/eds/src/components/Button/Button';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { StatusMessage } from '~/eds/src/components/StatusMessage/StatusMessage';
import { useDebouncedState } from '~/eds/src/hooks/useDebouncedState';
import { useConversationPipeline } from '~/features/ask-anything/hooks/useConversationPipeline';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { api } from '~/redux/api/createApi';
import { Nullable } from '~/types/Nullable';
import { Question, QuestionGroup } from '~/types/QuestionGroup';
import { Uuid } from '~/types/Uuid';

import { QuestionGroupList } from './QuestionGroupList';
import { SearchAndFilters } from './SearchAndFilters';
import { useQuestionGroupFormModal } from './useQuestionGroupFormModal';

export const QuestionGroups = ({ disableRun }: { disableRun?: boolean }) => {
  const intl = useIntl();
  const [debouncedSearch, search, setSearch] = useDebouncedState('');
  const [visibility, setVisibility] =
    useState<Nullable<'PRIVATE' | 'SHARED'>>(null);

  const isShareQuestionGroupEnabled = useFlag(FlagType.ShareQuestionGroup);

  const { toast } = useToast();
  const {
    data: questionGroups,
    isFetching,
    isError,
  } = api.endpoints.getQuestionGroups.useQuery({
    search: debouncedSearch,
    visibility,
  });

  const [questionGroupFormModal, openQuestionGroupFormModal] =
    useQuestionGroupFormModal({
      questionGroup: null,
    });

  const failedToLoadQuestionGroupsMessage = {
    status: 'warning' as const,
    title: intl.formatMessage({
      id: 'QuestionGroups.errorTitle',
      defaultMessage: 'Error: Unable to Load Saved Question Groups',
      description: 'Error title when question groups fail to load',
    }),
    message: intl.formatMessage({
      id: 'QuestionGroups.errorMessage',
      defaultMessage: 'Failed to load saved question groups.',
      description: 'Error message when question groups fail to load',
    }),
  };

  const onFinishQueue = () => {
    setTimeout(() => {
      clearQueue();
    }, 3000);
  };

  const {
    postMessage,
    clearQueue,
    parentId: runningQuestionGroupId,
  } = useConversationPipeline({
    onFinishQueue,
  });

  // Clear the queue when the component unmounts
  useEffect(() => {
    return clearQueue;
  }, []);

  useEffect(() => {
    if (isError) {
      toast(failedToLoadQuestionGroupsMessage);
    }
  }, [isError, toast, failedToLoadQuestionGroupsMessage]);

  const renderStatusMessage = () => {
    if (isError) {
      return <StatusMessage {...failedToLoadQuestionGroupsMessage} />;
    }

    if (!isFetching && debouncedSearch && questionGroups?.length === 0) {
      return (
        <StatusMessage
          status="info"
          title={intl.formatMessage({
            id: 'QuestionGroups.noSearchResultsTitle',
            defaultMessage: 'No saved question groups match the search',
            description: 'Title when no question groups match search criteria',
          })}
          message={intl.formatMessage({
            id: 'QuestionGroups.noSearchResultsMessage',
            defaultMessage: 'Try searching with a different text.',
            description:
              'Message when no question groups match search criteria',
          })}
        />
      );
    }

    if (!isFetching && !debouncedSearch && questionGroups?.length === 0) {
      return (
        <StatusMessage
          status="info"
          title={intl.formatMessage({
            id: 'QuestionGroups.noQuestionGroupsTitle',
            defaultMessage: 'No question groups yet',
            description: 'Title when no question groups exist',
          })}
          message={intl.formatMessage({
            id: 'QuestionGroups.noQuestionGroupsMessage',
            defaultMessage:
              'Create a question group by clicking on the Create a Question Group button below.',
            description: 'Message when no question groups exist',
          })}
        />
      );
    }

    return null;
  };

  const runQuestion = (question: Question, questionGroupId: Uuid) => {
    postMessage({
      id: question.id,
      message: question.text,
      parentId: questionGroupId,
    });
  };

  const runQuestionGroup = (
    questionGroup: QuestionGroup,
    questions: Question[],
  ) => {
    const messages = questions.map((question) => ({
      id: question.id,
      message: question.text,
      parentId: questionGroup.id,
    }));
    postMessage(messages);
  };

  return (
    <Layout direction="column" styles={componentStyles.mainContainer}>
      <Layout
        spacing={4}
        direction="column"
        styles={componentStyles.questionGroupsContainer}
      >
        <SearchAndFilters
          search={search}
          visibility={isShareQuestionGroupEnabled ? visibility : null}
          onFilter={isShareQuestionGroupEnabled ? setVisibility : undefined}
          onSearch={setSearch}
        />
        {renderStatusMessage() ?? (
          <ContentContainer
            loadingContent={{
              isLoading: isFetching,
              message: intl.formatMessage({
                id: 'QuestionGroups.loadingMessage',
                defaultMessage: 'Loading question groups...',
                description: 'Loading message for question groups',
              }),
            }}
          >
            <QuestionGroupList
              runningQuestionGroupId={runningQuestionGroupId}
              questionGroups={questionGroups || []}
              disableRun={!!runningQuestionGroupId || !!disableRun}
              onQuestionClick={runQuestion}
              onQuestionGroupClick={runQuestionGroup}
            />
          </ContentContainer>
        )}
      </Layout>

      <Layout
        justify="center"
        align="center"
        styles={componentStyles.createQuestionGroupButton}
      >
        <Button
          icon="plus"
          iconPosition="left"
          variant="action"
          text={intl.formatMessage({
            id: 'QuestionGroups.createQuestionGroupButton',
            defaultMessage: 'Create a Question Group',
            description: 'Button text to create a question group',
          })}
          onClick={openQuestionGroupFormModal}
        />
      </Layout>

      {questionGroupFormModal}
    </Layout>
  );
};

const componentStyles = {
  mainContainer: {
    overflow: 'hidden',
    height: '100%',
    justifyContent: 'space-between',
    width: '100%',
    position: 'relative',
    flex: 1,
  },
  questionGroupsContainer: {
    overflow: 'hidden',
    flex: 1,
    marginBottom: 8,
  },
  createQuestionGroupButton: {
    bottom: 0,
    width: '100%',
    position: 'absolute',
  },
};
